import { OPEN_AI_OPTIONS } from 'shared/constants/openai';
import {
  ICON_AI_ASSIST,
  ICON_AI_EXPAND,
  ICON_AI_GRAMMAR,
  ICON_AI_SHORTEN,
  ICON_AI_TRANSLATE,
} from 'dashboard/helper/commandbar/icons';

/**
 * AI actions that rewrite whatever the agent has already typed. They are built from
 * `event.data.content` alone on the server, so they work anywhere a draft exists — including the
 * compose modal, which has no conversation behind it yet.
 *
 * `icon` is an unocss class for `components-next/icon/Icon.vue`; `commandBarIcon` is the raw SVG
 * string ninja-keys needs.
 */
export const DRAFT_AI_ASSIST_OPTIONS = [
  {
    key: OPEN_AI_OPTIONS.TRANSLATE_TO_MAJORITY_LANGUAGE,
    labelKey: 'TRANSLATE_TO_MAJORITY_LANGUAGE',
    icon: 'i-lucide-languages',
    commandBarIcon: ICON_AI_TRANSLATE,
  },
  {
    key: 'rephrase',
    labelKey: 'REPHRASE',
    icon: 'i-lucide-wand-sparkles',
    commandBarIcon: ICON_AI_ASSIST,
  },
  {
    key: OPEN_AI_OPTIONS.FIX_SPELLING_GRAMMAR,
    labelKey: 'FIX_SPELLING_GRAMMAR',
    icon: 'i-lucide-spell-check',
    commandBarIcon: ICON_AI_GRAMMAR,
  },
  {
    key: OPEN_AI_OPTIONS.EXPAND,
    labelKey: 'EXPAND',
    icon: 'i-lucide-unfold-vertical',
    commandBarIcon: ICON_AI_EXPAND,
  },
  {
    key: OPEN_AI_OPTIONS.SHORTEN,
    labelKey: 'SHORTEN',
    icon: 'i-lucide-fold-vertical',
    commandBarIcon: ICON_AI_SHORTEN,
  },
  {
    key: OPEN_AI_OPTIONS.MAKE_FRIENDLY,
    labelKey: 'MAKE_FRIENDLY',
    icon: 'i-lucide-smile',
    commandBarIcon: ICON_AI_ASSIST,
  },
  {
    key: OPEN_AI_OPTIONS.MAKE_FORMAL,
    labelKey: 'MAKE_FORMAL',
    icon: 'i-lucide-briefcase',
    commandBarIcon: ICON_AI_ASSIST,
  },
  {
    key: OPEN_AI_OPTIONS.SIMPLIFY,
    labelKey: 'SIMPLIFY',
    icon: 'i-lucide-sparkles',
    commandBarIcon: ICON_AI_ASSIST,
  },
];

/**
 * The "Translate" entry, which is only offered once the customer's language has been detected —
 * the server resolves the target language from the conversation, so it needs one.
 */
export const TRANSLATE_AI_ASSIST_OPTION = {
  key: OPEN_AI_OPTIONS.TRANSLATE,
  labelKey: 'TRANSLATE',
  icon: 'i-lucide-languages',
  commandBarIcon: ICON_AI_TRANSLATE,
};

/**
 * Translation key for an option's label.
 * @param {string} labelKey
 * @returns {string}
 */
export const aiAssistLabelPath = labelKey =>
  `INTEGRATION_SETTINGS.OPEN_AI.OPTIONS.${labelKey}`;

/**
 * Builds the draft-based AI assist options, localized.
 *
 * @param {Function} t - vue-i18n translate function.
 * @param {string} [targetLanguageLabel] - Detected customer language. When present, "Translate
 *   (<language>)" is prepended; the compose modal has no conversation, so it passes nothing.
 * @returns {Array<{key: string, label: string, icon: string, commandBarIcon: string}>}
 */
export const buildDraftAIAssistOptions = (t, targetLanguageLabel = '') => {
  const options = targetLanguageLabel
    ? [
        {
          ...TRANSLATE_AI_ASSIST_OPTION,
          labelSuffix: ` (${targetLanguageLabel})`,
        },
        ...DRAFT_AI_ASSIST_OPTIONS,
      ]
    : DRAFT_AI_ASSIST_OPTIONS;

  return options.map(({ labelKey, labelSuffix = '', ...option }) => ({
    ...option,
    label: `${t(aiAssistLabelPath(labelKey))}${labelSuffix}`,
  }));
};
